/**
 * 민감도 격자 — 정의 2 × STALE 임계 4 × GAP_SUSPECT 2 = **16셀** (DESIGN §8.5).
 *
 * **격자의 축은 데이터가 아니라 명세가 정한다.** 응답에 셀이 두 개뿐이어도 16칸을
 * 다 그린다 — 축이 명세로 고정돼 있는데 데이터가 있는 칸만 그리면 "이 조합은
 * 애초에 없는 조합"과 "아직 계산하지 않은 조합"이 화면에서 같아 보인다. 없는 칸은
 * `—` 로 두고 몇 개인지 밝힌다 (결측을 0 으로 채우지 않는다).
 *
 * **왜 히트맵이 표 뷰를 대신하지 못하는가.** 칸 하나에 인쇄하는 값은 두 개(가동률 ·
 * 스냅샷 고장률)뿐이다. 신뢰구간·커버리지·T_obs·T_na·스냅샷 개체 수까지 칸에 넣으면
 * 격자가 아니라 빽빽한 표가 되어 "임계를 옮기면 어디가 진해지나"가 안 보인다.
 * 그래서 화면은 두 값만, 나머지는 표 뷰에 둔다 — 눌러도 같은 것이 나오지 않는다.
 *
 * **두 지표를 나란히 두되 같은 수로 쓰지 않는다** (§3.5). 시간 가중 가동률 A 는
 * 기간 지표(분모가 관측된 시간)이고 스냅샷 고장률 R_snap 은 한 시각의 개체 비율이다.
 * 1−A 와 R_snap 은 원리상 다른 값이며 서로의 검증에 쓸 수 없다. 그래서 색(음영)은
 * **A 하나만** 인코딩한다 — 두 지표를 같은 색 눈금에 태우면 비교 가능한 것처럼 보인다.
 */

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { Headline } from '@/components/WidgetShell'
import { fetchEnvelope, fetchPlain } from '@/lib/api'
import { DASH, int, minutes, pct, pp } from '@/lib/fmt'
import { MODE_LABEL, type Envelope, type ManifestData, type Mode } from '@/lib/types'
import { DEFAULT_FILTERS, filterKey, toQueryFilters, useFilters, type FilterState } from '@/stores/filters'

/** §3.4 가 요구한 격자. 클램프 하한 24h ~ 상한 168h 를 양 끝으로 갖는다. */
const THRESHOLDS = [24, 48, 72, 168] as const
/** §8.5 는 두 정의만 축으로 잡는다 — `user_facing` 을 넣으면 32셀이 된다 */
const GRID_MODES = ['strict', 'standard'] as const
const GAPS = ['included', 'excluded'] as const
const N_CELLS = GRID_MODES.length * THRESHOLDS.length * GAPS.length
/** 축의 양 끝. 임계 이동은 늘 "가장 의심하는 선택 ↔ 가장 믿는 선택" 사이로 본다 */
const LO_H = Math.min(...THRESHOLDS)
const HI_H = Math.max(...THRESHOLDS)

type GridMode = (typeof GRID_MODES)[number]
type GapAxis = (typeof GAPS)[number]

const GAP_LABEL: Record<GapAxis, string> = { included: '포함', excluded: '제외' }

/**
 * 음영 4단계 — 백엔드 리포트의 `_SHADES` 와 같은 단수다 (`analysis/reconcile.py::_ramp`).
 *
 * 진한 쪽(`SEQ_BLUE` 의 `#3a7fc4`·`#0b4f8f`)을 쓰지 않는 이유: 칸 안에 숫자를 직접
 * 인쇄하므로 배경이 어두워지면 글자 대비가 무너진다. 이 네 단계는 모두 `--c-ink`
 * 기준 10:1 이상을 유지한다 — 음영은 순서를 말하고 값은 글자가 말한다.
 */
const SHADES = ['#f2f7fd', '#dbe9f8', '#c2daf2', '#a9cbee'] as const

interface Cell {
  mode: string
  stale_threshold_h: number
  gap_suspect: string
  availability: number | null
  availability_ci?: [number, number] | null
  coverage?: number | null
  n_chargers?: number | null
  n_low_coverage?: number | null
  t_up_min?: number | null
  t_down_min?: number | null
  t_na_min?: number | null
  t_obs_min?: number | null
  snapshot_failure_rate?: number | null
  snapshot_up?: number | null
  snapshot_down?: number | null
  snapshot_na?: number | null
  snapshot_unobserved?: number | null
  snapshot_excluded_gap?: number | null
  /** 구 빌드의 `t_na_min` — 같은 양이므로 그대로 받는다 */
  na_minutes?: number | null
  /**
   * **`down_share` 는 스냅샷 고장률이 아니다.** 실측 응답에서 이 값이 1 − availability
   * 와 소수점까지 일치했다 — 즉 시간 가중 값이다. 이 카드가 경고하는 바로 그 혼동이라
   * R_snap 자리에 절대 넣지 않는다. 필드를 타입에 남기는 이유는 "있는데 안 쓴다"가
   * 결정이고, 지우면 다음 사람이 다시 이어 붙이기 때문이다.
   */
  down_share?: number | null
}

interface SensitivityData {
  cells: Cell[]
  grid_complete: boolean
  n_cells?: number
  report?: string
}

const keyOf = (mode: string, h: number, gap: string) => `${mode}|${h}|${gap}`

/**
 * **이 격자는 상단 필터를 타지 않는다.** `reconcile_grid` 는 전국 전량으로 미리
 * 계산해 둔 산출물이고 `/reconcile/sensitivity` 는 그것을 그대로 읽는다 —
 * `busi`·`zcode` 를 붙여 요청해도 응답이 한 바이트도 달라지지 않는 것을 확인했다.
 *
 * 그런데 필터 바는 전 페이지 공통이라 칩이 걸린 채로 이 화면에 온다. 아무 말도
 * 안 하면 "GS차지비 · 서울의 가동률 99.06%" 로 읽힌다 — 전국 값인데. 그래서
 * 무엇이 걸렸는지 **이름으로** 되짚어 준다. "필터가 적용되지 않습니다" 만으로는
 * 자기가 무엇을 걸었는지 기억하지 못한 사람에게 아무것도 알려주지 못한다.
 */
function ignoredFilterNames(f: FilterState): string[] {
  const on: string[] = []
  if (f.busi.length > 0) on.push('사업자')
  if (f.zcode.length > 0 || f.zscode.length > 0) on.push('지역')
  if (f.maker.length > 0) on.push('제조사')
  if (f.chgerType.length > 0) on.push('충전기 타입')
  if (f.kind.length > 0) on.push('설치 환경')
  if (f.yearFrom != null || f.yearTo != null) on.push('설치 연식')
  if (f.floor != null) on.push('설치 층')
  if (f.from != null || f.to != null) on.push('기간')
  if (f.minCoverage !== DEFAULT_FILTERS.minCoverage) on.push('커버리지 하한')
  return on
}

/** NA 시간(분). 신 필드 → 구 필드 순으로 본다. 없으면 판정 불가지 0 이 아니다. */
function naMin(c: Cell | undefined): number | null {
  if (!c) return null
  return c.t_na_min ?? c.na_minutes ?? null
}

/** 상대 변화율. 부호를 항상 붙인다 — 방향이 곧 의미다. */
function rel(v: number | null): string {
  if (v == null) return DASH
  return `${v >= 0 ? '+' : '−'}${(Math.abs(v) * 100).toFixed(0)}%`
}

interface Shift {
  dAvail: number | null
  dNa: number | null
}

/**
 * 임계 이동을 문장으로. **가동률만 말하면 "안 변했다"로 읽힌다** — 같은 이동에서
 * T_na 가 얼마나 달라졌는지를 같은 문장 안에 둬야 임계의 의미가 전달된다.
 */
function shiftSentence(shift: Shift, present: readonly number[]): string {
  if (shift.dAvail != null && shift.dNa != null) {
    return `${LO_H}h → ${HI_H}h 로 옮기면 가동률은 ${pp(shift.dAvail)} 움직이는데, 같은 이동에서 모르는 시간 T_na 는 ${rel(
      shift.dNa,
    )} 달라집니다. 가동률만 보면 임계를 어디 긋든 상관없어 보이지만, 실제로 바뀌는 것은 "얼마나 잘 돌아갔나"가 아니라 "무엇을 모른다고 할 것인가" 입니다.`
  }
  if (shift.dAvail != null) {
    return `${LO_H}h → ${HI_H}h 가동률 변화는 ${pp(
      shift.dAvail,
    )} 입니다. T_na 변화는 두 임계의 값이 모두 있어야 계산되므로 아직 ${DASH} 입니다 — 가동률이 거의 그대로여도 T_na 는 크게 달라질 수 있으니, 이 값만 보고 "임계는 상관없다"고 결론 내리지 마세요.`
  }
  const have = present.length > 0 ? `${present.map((h) => `${h}h`).join(' · ')}` : '없음'
  return `${LO_H}h 와 ${HI_H}h 셀이 모두 있어야 계산됩니다 — 지금 격자에 있는 임계는 ${have} 뿐이라 이 열이 전부 ${DASH} 로 남습니다. 임계를 올리면 STALE 로 잘라내는 꼬리가 짧아지는 동시에 집계 창의 왼쪽 끝(첫 관측 − H)이 더 과거로 열리므로 (§3.5.1), 가동률이 거의 그대로여도 T_na 는 크게 달라질 수 있습니다.`
}

export function SensitivityGrid() {
  const filters = useFilters()

  const q = useQuery({
    queryKey: ['reconcile/sensitivity', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<SensitivityData>('/reconcile/sensitivity', toQueryFilters(filters), {}, signal),
    staleTime: 5 * 60 * 1000,
  })

  // 게이트 판정값은 **빌드가 실제로 쓴 값**이다. 프론트가 24 를 상수로 박으면
  // 다음 빌드가 임계를 바꿔도 화면은 계속 24 를 가리킨다.
  const manifestQ = useQuery({
    queryKey: ['meta', 'manifest'],
    queryFn: ({ signal }) => fetchPlain<ManifestData>('/meta/manifest', signal),
    staleTime: 60 * 1000,
  })

  const env = q.data as Envelope<SensitivityData> | undefined
  const cells = useMemo(() => env?.data.cells ?? [], [env])
  const gridComplete = env?.data.grid_complete ?? false

  const byKey = useMemo(() => {
    const m = new Map<string, Cell>()
    for (const c of cells) m.set(keyOf(c.mode, c.stale_threshold_h, c.gap_suspect), c)
    return m
  }, [cells])

  const nFilled = useMemo(() => {
    let n = 0
    for (const mode of GRID_MODES) {
      for (const h of THRESHOLDS) {
        for (const gap of GAPS) if (byKey.has(keyOf(mode, h, gap))) n += 1
      }
    }
    return n
  }, [byKey])

  const presentThresholds = useMemo(
    () => THRESHOLDS.filter((h) => cells.some((c) => c.stale_threshold_h === h)),
    [cells],
  )

  /* ── 현재 빌드가 쓰는 셀 (요구 4) ───────────────────────────────── */
  const gates = manifestQ.data?.gates as Record<string, unknown> | undefined
  const rawThreshold = gates?.['stale_threshold_h']
  const buildThreshold = typeof rawThreshold === 'number' ? rawThreshold : null
  const currentMode: Mode = filters.mode
  // 격자 축에 없는 정의(`user_facing`)를 고르면 가리킬 셀이 없다. 그때는 조용히
  // 아무 칸도 강조하지 않는 대신 왜 없는지 문장으로 말한다.
  const modeOnAxis = (GRID_MODES as readonly string[]).includes(currentMode)
  // 이 빌드는 GAP_SUSPECT 를 걸러내지 않은 값을 쓴다 — 격자에서는 '포함' 열이다
  const CURRENT_GAP: GapAxis = 'included'
  const currentKey =
    modeOnAxis && buildThreshold != null && (THRESHOLDS as readonly number[]).includes(buildThreshold)
      ? keyOf(currentMode, buildThreshold, CURRENT_GAP)
      : null
  const currentCell = currentKey ? byKey.get(currentKey) : undefined

  /* ── 커버리지 마이크로바가 말할 수 있는 값 ───────────────────────
   *
   * **이 엔드포인트의 `meta.coverage` 는 1.0 고정 자리표시자다** (라우터가 그렇게
   * 쓴다). 그대로 걸면 마이크로바가 "전부 관측했다"고 말하는데, 같은 카드의 표
   * 뷰가 바로 아래에서 48.9% 를 인쇄한다 — 한 화면 안에서 서로를 반박한다.
   *
   * 다만 여기서는 `hideCoverage` 로 지우면 안 된다. 헤드라인이 가동률이라
   * 커버리지 없는 가동률이 되어 §7.1 계약을 깬다. 헤드라인이 가리키는 셀의
   * 커버리지가 응답 안에 있으므로 그것으로 바꿔 단다 — 헤드라인과 바가 같은
   * 셀을 말하게 된다. 셀이 없으면 헤드라인도 —이므로 그때만 바를 내린다. */
  const cellCoverage =
    typeof currentCell?.coverage === 'number' && Number.isFinite(currentCell.coverage)
      ? currentCell.coverage
      : null
  const frameMeta = env ? { ...env.meta, coverage: cellCoverage ?? env.meta.coverage } : undefined

  /* ── 음영 눈금 — 격자 안의 상대 크기 (백엔드 리포트와 같은 규칙) ── */
  const ramp = useMemo(() => {
    const present = cells
      .map((c) => c.availability)
      .filter((v): v is number => v != null && Number.isFinite(v))
    if (present.length === 0) return null
    const lo = Math.min(...present)
    const hi = Math.max(...present)
    return { lo, hi, flat: hi <= lo }
  }, [cells])

  const shadeOf = (v: number | null | undefined): string | undefined => {
    if (v == null || ramp == null) return undefined
    if (ramp.flat) return SHADES[SHADES.length - 1]
    const step = Math.round(((v - ramp.lo) / (ramp.hi - ramp.lo)) * (SHADES.length - 1))
    return SHADES[Math.max(0, Math.min(SHADES.length - 1, step))]
  }

  /* ── 임계 이동 24h → 168h (요구 3) ──────────────────────────────── */
  const shiftOf = (mode: GridMode, gap: GapAxis): Shift => {
    const lo = byKey.get(keyOf(mode, LO_H, gap))
    const hi = byKey.get(keyOf(mode, HI_H, gap))
    const a0 = lo?.availability ?? null
    const a1 = hi?.availability ?? null
    const na0 = naMin(lo)
    const na1 = naMin(hi)
    return {
      dAvail: a0 != null && a1 != null ? a1 - a0 : null,
      dNa: na0 != null && na1 != null && na0 > 0 ? (na1 - na0) / na0 : null,
    }
  }
  const shiftHere = shiftOf(modeOnAxis ? (currentMode as GridMode) : 'standard', CURRENT_GAP)

  /* ── GAP 축이 값을 바꾸는가 (요구 2) ───────────────────────────── */
  const gapCheck = useMemo(() => {
    let pairs = 0
    let maxDiff = 0
    for (const mode of GRID_MODES) {
      for (const h of THRESHOLDS) {
        const inc = byKey.get(keyOf(mode, h, 'included'))
        const exc = byKey.get(keyOf(mode, h, 'excluded'))
        if (inc?.availability == null || exc?.availability == null) continue
        pairs += 1
        maxDiff = Math.max(maxDiff, Math.abs(inc.availability - exc.availability))
      }
    }
    const known = cells
      .map((c) => c.snapshot_excluded_gap)
      .filter((v): v is number => typeof v === 'number')
    return {
      pairs,
      maxDiff,
      nKnown: known.length,
      allZero: known.length > 0 && known.every((v) => v === 0),
    }
  }, [byKey, cells])

  /**
   * 격자가 실제로 쓴 충전기 수. 실측에서 이 값(523,527)이 페이지 상단 모집단
   * (464,141)과 다르다 — 격자는 정규 빌드가 아니라 전용 명령이 만든 산출물이라
   * 폭포(§3.6)를 통과한 집합과 분모가 같지 않다. 어디에도 안 적으면 상단 모집단이
   * 이 격자의 분모라고 읽히므로 표에 열을 두고 주의사항으로 못을 박는다.
   */
  const gridN = cells.find((c) => typeof c.n_chargers === 'number')?.n_chargers ?? null
  const popN = env?.meta.population_n ?? null

  const ignored = ignoredFilterNames(filters)

  /* ── 표 뷰 — 화면이 인쇄하지 않는 값들이 여기 있다 ──────────────── */
  const rows = GRID_MODES.flatMap((mode) =>
    GAPS.flatMap((gap) => THRESHOLDS.map((h) => ({ mode, gap, h, cell: byKey.get(keyOf(mode, h, gap)) }))),
  )

  const table = (
    /*
     * `.ctable` 은 340px 에서 세로로 잘려 스크롤된다 (16행이 521px). 스크롤되는데
     * 포커스를 받을 수 없으면 키보드만 쓰는 사람이 잘린 아래쪽 행에 닿지 못한다 —
     * axe `scrollable-region-focusable`(serious) 가 정확히 이것을 잡는다. 차트 뷰만
     * 검사하면 안 보이므로 표 뷰에서도 axe 를 돌리게 해 두었다 (e2e).
     */
    <div className="ctable" tabIndex={0} role="region" aria-label="민감도 격자 16셀 표">
      <table className="data-table">
        <caption className="sr-only">
          민감도 격자 16셀 — 정의 · STALE 임계 · GAP_SUSPECT 별 가동률과 스냅샷 고장률
        </caption>
        <thead>
          <tr>
            <th scope="col">정의</th>
            <th scope="col">STALE</th>
            <th scope="col">GAP</th>
            {/* 격자의 분모다. 페이지 상단 모집단과 다를 수 있어 반드시 읽을 수 있어야 한다 */}
            <th scope="col" className="ta-r">충전기 N</th>
            <th scope="col" className="ta-r">가동률 A</th>
            <th scope="col" className="ta-r">A 95% 구간</th>
            <th scope="col" className="ta-r">커버리지</th>
            <th scope="col" className="ta-r">T_obs</th>
            <th scope="col" className="ta-r">T_na</th>
            <th scope="col" className="ta-r">C&lt;기준</th>
            <th scope="col" className="ta-r">R_snap</th>
            <th scope="col" className="ta-r">UP</th>
            <th scope="col" className="ta-r">DOWN</th>
            <th scope="col" className="ta-r">NA</th>
            <th scope="col" className="ta-r">미관측</th>
            <th scope="col" className="ta-r">GAP제외</th>
            <th scope="col">비고</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ mode, gap, h, cell }) => {
            const ci = cell?.availability_ci
            const isCurrent = currentKey === keyOf(mode, h, gap)
            return (
              <tr key={keyOf(mode, h, gap)}>
                <td className="mono">{mode}</td>
                <td className="num ta-r">{h}h</td>
                <td>{GAP_LABEL[gap]}</td>
                <td className="num ta-r">{int(cell?.n_chargers)}</td>
                <td className="num ta-r">{pct(cell?.availability)}</td>
                <td className="num ta-r">{ci ? `${pct(ci[0])} ~ ${pct(ci[1])}` : DASH}</td>
                <td className="num ta-r">{pct(cell?.coverage, 1)}</td>
                <td className="num ta-r">{minutes(cell?.t_obs_min)}</td>
                <td className="num ta-r">{minutes(naMin(cell))}</td>
                <td className="num ta-r">{int(cell?.n_low_coverage)}</td>
                <td className="num ta-r">{pct(cell?.snapshot_failure_rate)}</td>
                <td className="num ta-r">{int(cell?.snapshot_up)}</td>
                <td className="num ta-r">{int(cell?.snapshot_down)}</td>
                <td className="num ta-r">{int(cell?.snapshot_na)}</td>
                <td className="num ta-r">{int(cell?.snapshot_unobserved)}</td>
                <td className="num ta-r">{int(cell?.snapshot_excluded_gap)}</td>
                <td>{isCurrent ? '현재 빌드가 쓰는 셀' : cell ? '' : '아직 계산되지 않음'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )

  /* ── 범례 — 음영은 상대 크기이므로 경계값을 인쇄한다 ─────────────
   *
   * **화면에 없는 마크는 범례에도 없다.** 격자가 다 차 있는데 "아직 계산되지 않은
   * 셀" 견본을 걸어 두면 어딘가에 그런 칸이 있다고 찾게 만든다. 범례는 지금 이
   * 그림에 실제로 나온 것만 설명한다. */
  const shadeLegend =
    // 모든 칸의 A 가 없으면 음영이 아예 칠해지지 않는다 — 그때는 눈금을 걸지 않는다
    ramp == null
      ? []
      : ramp.flat
        ? // 값이 하나뿐이면 단계가 성립하지 않는다. 네 칸을 같은 라벨로 늘어놓으면
          // 있지도 않은 순서를 읽게 되므로 한 칸으로 접는다
          [
            {
              // 리터럴 인덱스라야 타입이 string 으로 좁혀진다 (뒤 항이 닿는 일은 없다)
              color: SHADES[SHADES.length - 1] ?? SHADES[0],
              pattern: 'solid' as const,
              label: `${pct(ramp.lo)} — 격자의 값이 하나뿐이라 음영이 순서를 말하지 않습니다`,
            },
          ]
        : SHADES.map((color, i) => {
            const width = (ramp.hi - ramp.lo) / (SHADES.length - 1)
            const from = i === 0 ? ramp.lo : ramp.lo + (i - 0.5) * width
            const to = i === SHADES.length - 1 ? ramp.hi : ramp.lo + (i + 0.5) * width
            return { color, pattern: 'solid' as const, label: `${pct(from)} ~ ${pct(to)}` }
          })

  const legend = [
    ...shadeLegend,
    ...(nFilled < N_CELLS
      ? [{ color: 'var(--c-na)', pattern: 'hatch' as const, label: '아직 계산되지 않은 셀 (—)' }]
      : []),
    ...(currentKey != null
      ? [{ color: 'var(--c-accent)', pattern: 'ring' as const, label: '현재 빌드가 쓰는 셀' }]
      : []),
  ]

  const currentLabel = modeOnAxis
    ? `${currentMode} · ${buildThreshold ?? DASH}h · GAP ${GAP_LABEL[CURRENT_GAP]}`
    : `${currentMode} 는 격자 축이 아님`

  return (
    <ChartFrame
      title="민감도 격자 16셀"
      question="정의·임계·수집공백 처리를 바꾸면 전국 숫자가 얼마나 움직이나 — 그리고 무엇이 안 움직이나"
      meta={frameMeta}
      // 헤드라인이 —이면 붙일 커버리지가 없다. 1.0 자리표시자를 대신 그리지 않는다
      hideCoverage={cellCoverage == null}
      headline={
        <Headline value={pct(currentCell?.availability)} unit={`현재 빌드 셀 · ${currentLabel}`} />
      }
      legend={cells.length > 0 ? legend : undefined}
      /* **격자를 안 그렸으면 격자를 읽는 법도 주지 않는다.** 산출물이 없어 칸이
       * 하나도 없는데 "가로로 STALE 임계를 … 그렸습니다" 라고 적으면 그 줄 자체가
       * 거짓이 된다. 같은 폴더의 `PanelResult` 가 미식별 상태에서 쓰는 규칙과 같다. */
      reading={
        cells.length > 0
          ? '가로로 STALE 임계를, 세로로 고장 정의와 수집 공백 처리를 놓고 16개 조합의 전국 숫자를 한 칸씩 그렸습니다. 칸이 진할수록 그 조합에서 가동률이 높다는 뜻이며, 진하기는 이 격자 안에서만 통하는 상대 크기입니다.'
          : '아직 그릴 격자가 없습니다. 축과 칸이 무엇을 뜻하는지는 산출물이 생겨 격자를 그리는 순간에 적습니다 — 그리지 않은 그림의 눈금을 설명하지 않습니다.'
      }
      encoding={cells.length > 0 ? [
        {
          role: '가로축',
          what: 'STALE 임계 (24 · 48 · 72 · 168시간)',
          detail:
            '마지막 statUpdDt 로부터 이 시간이 지나면 그 상태를 더는 현재로 믿지 않고 NA 로 재분류합니다 (§3.4). 24h 는 클램프 하한이고 168h 는 상한이라, 이 축의 양 끝이 곧 "가장 의심하는 선택"과 "가장 믿는 선택"입니다.',
        },
        {
          role: '세로축',
          what: '고장 정의 2종 × GAP_SUSPECT 처리 2종 (4행)',
          detail:
            'strict 는 통신이상(1)을 DOWN 으로 세지 않아 DOWN 이 줄고 NA 가 늡니다 — 설비가 다른 것이 아니라 정의가 다른 것입니다. 정의와 임계를 동시에 바꾼 두 칸의 차이는 원인을 분리할 수 없으므로, 비교는 같은 열 안에서 하세요.',
        },
        {
          role: '칸 음영',
          what: '시간 가중 가동률 A — 이 격자 안의 상대 크기',
          detail:
            '색은 A 하나만 나타냅니다. 두 지표를 같은 색 눈금에 태우면 서로 비교할 수 있는 값처럼 보이기 때문입니다. 경계값은 위 범례에 인쇄했으며, 절대 수준이 아니라 이 표 안에서의 순서만 말합니다.',
        },
        {
          role: '칸 첫 줄',
          what: '가동률 A = ΣT_up / ΣT_obs (기간 지표)',
          detail:
            '분모는 관측된 시간이며 관측하지 못한 시간(NA)은 분모에서 뺍니다. 충전기별 비율의 단순평균이 아니라 분자·분모를 각각 합산한 값입니다 (§3.5 원칙 ①).',
        },
        {
          role: '칸 둘째 줄',
          what: '스냅샷 고장률 R_snap = |DOWN| / |UP∪DOWN| (시점 지표)',
          detail:
            '한 시각의 개체 수 비율이며 공표 통계 대조 전용입니다. 오래 고장난 1기와 잠깐 고장난 1기가 같은 무게를 갖습니다 — 그래서 1−A 와 원리상 다른 값이고, 둘을 서로의 검증에 쓰면 안 됩니다.',
        },
        {
          role: '오른쪽 끝 열',
          what: `임계 ${LO_H}h → ${HI_H}h 이동량 (ΔA · ΔT_na)`,
          detail:
            '이 페이지의 핵심입니다. ΔA 만 보면 임계를 옮겨도 거의 안 변한 것처럼 보이지만, 같은 이동에서 T_na 는 크게 달라집니다 — 임계는 "얼마나 잘 돌아갔나"보다 "무엇을 모른다고 할 것인가"를 바꿉니다.',
        },
        {
          role: '굵은 테두리',
          what: '현재 빌드가 쓰는 셀',
          detail:
            'manifest 의 gates.stale_threshold_h 와 상단 모드 토글이 함께 가리키는 칸이며, 이 대시보드의 다른 모든 화면이 쓰는 값입니다. 임계를 상수로 박지 않고 manifest 에서 읽으므로 빌드가 임계를 바꾸면 테두리도 따라 움직입니다.',
        },
        {
          role: '— (대시)',
          what: '아직 계산되지 않은 셀',
          detail:
            '0 이 아니라 판정 불가입니다. 축은 명세가 정하므로 16칸을 다 그리고, 값이 없는 칸은 해칭과 대시로 남깁니다 — 그리지 않으면 "애초에 없는 조합"과 구분되지 않습니다.',
        },
      ] : undefined}
      /*
       * **본문이 아니라 `controls` 자리에 둔다.** 이 고지는 표 뷰에서 더 필요한데
       * (표는 소수점까지 읽는 화면이다) 본문에 두면 표로 바꾸는 순간 사라진다 —
       * `ChartFrame` 이 정렬·지표 컨트롤을 본문 밖으로 뺀 것과 같은 이유다.
       * 조작이 아닌 것을 조작 자리에 넣는 게 마음에 걸리지만, 뷰와 무관하게 항상
       * 보이는 자리가 여기뿐이고 "전국 값인데 필터 값으로 읽는" 오독이 더 크다.
       */
      controls={
        ignored.length > 0 ? (
          <p className="sgrid__banner sgrid__banner--persist" role="status" data-testid="filters-ignored">
            <strong>걸려 있는 필터({ignored.join(' · ')})가 이 격자에는 적용되지 않았습니다.</strong>{' '}
            아래 값은 <strong>전국 전량</strong>입니다. 격자는 전용 명령이 미리 계산해 둔 산출물이라
            요청 시 필터로 다시 계산하지 않습니다 (§7.3). 상단 모드 토글만 <em>강조할 칸</em>을
            고르는 데 쓰이며, 그것도 칸의 값을 바꾸지는 않습니다.
          </p>
        ) : undefined
      }
      isLoading={q.isLoading}
      error={q.error as Error | null}
      onRetry={() => void q.refetch()}
      table={cells.length > 0 ? table : undefined}
      notes={[
        {
          label: '두 지표를 같은 수로 쓰지 마라.',
          text: '가동률 A 는 기간 지표(관측 시간이 분모), 스냅샷 고장률 R_snap 은 한 시각의 개체 비율입니다. 1−A 와 R_snap 은 원리상 다른 값이며 서로의 검증에도 쓸 수 없습니다 (§3.5).',
        },
        {
          label: 'GAP_SUSPECT 축이 죽어 보이는 것은 버그가 아니다.',
          text: '수집이 깨끗해 관측 공백과 겹친 구간이 사실상 없으면 포함 열과 제외 열이 같은 값이 됩니다. 그래도 축을 지우지 않는 이유는 "확인해 봤더니 안 바뀌더라"가 결과이기 때문입니다.',
        },
        {
          label: '임계는 두 방향으로 작용한다.',
          text: '임계를 올리면 STALE 로 잘라내는 꼬리가 짧아지는 동시에 집계 창의 왼쪽 끝(첫 관측 − H)이 더 과거로 열립니다 (§3.5.1). 그래서 T_na 가 임계에 대해 단조 감소한다는 보장이 없습니다.',
        },
        {
          label: '커버리지 미달 충전기를 격자에서 빼지 않는다.',
          text: '커버리지 필터는 임계와 같이 움직여서(임계↑ → 커버리지↑ → 통과 개체↑) 두 효과가 섞이면 방향을 읽을 수 없게 됩니다. 개수만 세고 제외하지 않습니다 — 순위·비교 화면에서는 §3.5 대로 분리합니다.',
        },
        cells.some((c) => c.down_share != null && c.snapshot_failure_rate == null)
          ? {
              label: '응답의 down_share 를 R_snap 자리에 넣지 않았다.',
              text: '실측 응답에서 down_share 는 1−가동률과 소수점까지 같은 시간 가중 값입니다. 스냅샷 고장률이 아니므로 R_snap 칸은 —로 두었습니다. 이 카드가 경고하는 혼동이 정확히 그것입니다.',
            }
          : '',
        {
          label: '이 격자는 상단 필터를 타지 않는다.',
          text: '전국 전량으로 미리 계산해 둔 산출물을 그대로 읽기 때문에 사업자·지역·제조사 등을 걸어도 값이 달라지지 않습니다. 필터가 걸려 있으면 카드 위에 무엇이 무시됐는지 적습니다 — 고장 정의(모드)만 강조할 칸을 고르는 데 쓰입니다.',
        },
        gridN != null && popN != null && gridN !== popN
          ? {
              label: '격자의 충전기 수와 페이지 상단 모집단이 다르다.',
              text: `격자는 ${int(gridN)}기, 상단 모집단은 ${int(popN)}기(§3.6 폭포를 통과한 수)입니다. 격자는 정규 빌드가 아니라 전용 명령이 만든 산출물이라 분모가 같지 않으니, 이 화면의 값을 다른 화면의 값과 소수점까지 맞춰 읽지 마세요. 셀별 N 은 표 뷰에 있습니다.`,
            }
          : '',
        cellCoverage != null
          ? {
              label: '카드 오른쪽 위 커버리지는 현재 빌드 셀의 값이다.',
              text: `이 엔드포인트의 meta.coverage 는 1.0 고정 자리표시자라 그대로 그리면 "전부 관측했다"가 됩니다. 헤드라인이 가리키는 셀의 커버리지 ${pct(cellCoverage, 1)} 로 바꿔 달았습니다 — 다른 셀은 임계마다 커버리지가 다르며 표 뷰에 전부 있습니다.`,
            }
          : '',
        nFilled < N_CELLS
          ? {
              label: `채워진 셀 ${nFilled}/${N_CELLS}.`,
              text: '나머지 칸은 0 이 아니라 아직 계산되지 않은 상태입니다. 격자를 채우는 명령은 카드 안 안내에 있습니다.',
            }
          : '',
      ]}
    >
      {cells.length === 0 ? (
        // 요구 5 — 빈 히트맵을 그리지 않는다. 회색 16칸은 "값이 전부 같다"로 읽힌다
        <div className="sgrid__empty verdict-note" role="status">
          <p>
            <strong>격자가 비어 있습니다.</strong> 16셀은 구간 전량이 필요해 실측 189초가
            걸리므로 API 가 요청 시 계산하지 않습니다 (§7.3). 아래 명령이 산출물을 만듭니다.
          </p>
          <p className="sgrid__cmd">
            <code>evca etl reconcile</code>
          </p>
          <p className="verdict-note__hint">
            빈 격자를 그리는 대신 비었다고 말합니다 — 값 없는 16칸은 "값이 전부 같다"로 읽힙니다.
          </p>
        </div>
      ) : (
        <>
          {!gridComplete && (
            <p className="sgrid__banner" role="status">
              <strong>
                격자가 불완전합니다 — {nFilled}/{N_CELLS}셀.
              </strong>{' '}
              나머지 칸은 0 이 아니라 아직 계산되지 않았습니다. <code>evca etl reconcile</code> 이
              채웁니다 (요청 시 계산은 189초라 API 가 하지 않습니다).
            </p>
          )}

          <table className="sgrid">
            <caption className="sr-only">
              민감도 격자 — 가로는 STALE 임계, 세로는 고장 정의와 GAP_SUSPECT 처리입니다. 칸마다
              시간 가중 가동률과 스냅샷 고장률을 함께 인쇄했습니다.
            </caption>
            <thead>
              <tr>
                <th scope="col" className="sgrid__corner">
                  정의 · GAP_SUSPECT
                </th>
                {THRESHOLDS.map((h) => (
                  <th scope="col" key={h} className="sgrid__colhead num">
                    {h}h
                  </th>
                ))}
                <th scope="col" className="sgrid__colhead sgrid__colhead--shift">
                  {LO_H}h → {HI_H}h 이동
                </th>
              </tr>
            </thead>
            <tbody>
              {GRID_MODES.flatMap((mode) =>
                GAPS.map((gap) => {
                  const shift = shiftOf(mode, gap)
                  return (
                    <tr key={`${mode}|${gap}`}>
                      <th scope="row" className="sgrid__rowhead">
                        <span className="sgrid__mode">
                          {MODE_LABEL[mode]} <code>{mode}</code>
                        </span>
                        <span className="sgrid__gap">GAP_SUSPECT {GAP_LABEL[gap]}</span>
                      </th>

                      {THRESHOLDS.map((h) => {
                        const cell = byKey.get(keyOf(mode, h, gap))
                        const isCurrent = currentKey === keyOf(mode, h, gap)
                        const shade = shadeOf(cell?.availability)
                        return (
                          <td
                            key={h}
                            className={`sgrid__cell${
                              cell ? '' : ' sgrid__cell--missing hatch-missing'
                            }${isCurrent ? ' is-current' : ''}`}
                            style={shade ? { background: shade } : undefined}
                          >
                            <span className="sgrid__a num">{pct(cell?.availability)}</span>
                            <span className="sgrid__snap num">
                              R_snap {pct(cell?.snapshot_failure_rate)}
                            </span>
                            {isCurrent && <span className="sgrid__now">현재 빌드</span>}
                          </td>
                        )
                      })}

                      <td className="sgrid__shift">
                        <span className="sgrid__a num">
                          {shift.dAvail == null ? DASH : pp(shift.dAvail)}
                        </span>
                        <span className="sgrid__snap num">T_na {rel(shift.dNa)}</span>
                      </td>
                    </tr>
                  )
                }),
              )}
            </tbody>
          </table>

          {/* 요구 3 — 가동률만 보면 "안 변했다"로 읽히는 것을 문장이 막는다 */}
          <p className="sgrid__shiftnote" data-testid="threshold-shift-note">
            <strong>임계를 어디 긋느냐가 무엇을 바꾸는가.</strong>{' '}
            {shiftSentence(shiftHere, presentThresholds)}
          </p>

          {/* 요구 2 — 축이 죽은 것처럼 보이는 이유. 버그로 오해하면 안 된다 */}
          <p className="sgrid__gapnote" data-testid="gap-axis-note">
            <strong>GAP_SUSPECT 축은 값을 바꾸지 않습니다 — 축이 고장난 것이 아닙니다.</strong>{' '}
            수집이 깨끗해서(관측 공백과 겹친 상태 구간이 사실상 없어) 포함 열과 제외 열이 같은
            값이 됩니다.{' '}
            {gapCheck.pairs > 0
              ? `대조 가능한 쌍 ${gapCheck.pairs}개 중 최대 차이는 ${
                  gapCheck.maxDiff === 0 ? '0 — 두 열이 완전히 동일' : pp(gapCheck.maxDiff)
                } 입니다.`
              : '다만 지금 격자에는 제외 열 셀이 없어, 이 화면에서 두 열을 직접 대조하지는 못합니다.'}
            {gapCheck.allZero
              ? ` 응답의 snapshot_excluded_gap 도 아는 셀 ${gapCheck.nKnown}개 전부 0 입니다 — 스냅샷에서 빠진 개체가 하나도 없다는 뜻입니다.`
              : ''}
          </p>

          {!modeOnAxis && (
            <p className="sgrid__offaxis" role="status">
              상단 모드 토글이 <strong>{MODE_LABEL[currentMode]}</strong>(
              <code>{currentMode}</code>)를 가리키고 있는데, 이 정의는 격자 축이 아니라서 강조할
              칸이 없습니다. limitYn 오버레이를 임계·GAP 과 교차시키면 축이 하나 늘어 32셀이
              되므로 §8.5 는 두 정의만 축으로 잡습니다.
            </p>
          )}
        </>
      )}
    </ChartFrame>
  )
}
